import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  RESOURCE_KINDS,
  type ManualResourceInput,
  type RegistryResource,
  type ResourceKind,
} from '../../../api/src/registry/registry.types';
import { api } from '../api/client';
import { useCanEdit } from '../components/EditorOnly';
import { Stack, Typography } from '../components/mui-system';

const names: Record<ResourceKind, string> = {
  domain: 'Сайт (весь домен)',
  website: 'Отдельная страница сайта',
  channel: 'Канал',
  account: 'Аккаунт',
  publication: 'Публикация',
};
const owners = { owned: 'Наш', third_party: 'Сторонний', unknown: 'Не установлена' };
const roleNames = { donor: 'Донор', reference: 'Образец', competitor: 'Конкурент' };
export function RegistryResourceDialog({
  workspaceId,
  resource,
  initialKind,
  initialValues,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  resource?: RegistryResource;
  initialKind: ResourceKind;
  initialValues?: Partial<ManualResourceInput>;
  onClose: () => void;
  onSaved: (row: RegistryResource, archived?: boolean) => void;
}) {
  const canEdit = useCanEdit(resource ? 'registry.edit' : 'registry.create');
  const canDelete = useCanEdit('registry.delete');
  const [input, setInput] = useState<ManualResourceInput>(() => ({
    kind: resource?.kind ?? initialKind,
    address: resource?.address ?? '',
    title: resource?.title ?? '',
    ownership: resource?.ownership ?? 'unknown',
    roles:
      resource?.roles.filter(
        (role) => role === 'donor' || role === 'reference' || role === 'competitor',
      ) ?? [],
    notes: resource?.notes ?? '',
    ...(!resource ? initialValues : {}),
  }));
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const mounted = useRef(true),
    saving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  function change<K extends keyof ManualResourceInput>(key: K, value: ManualResourceInput[K]) {
    setInput((previous) => ({ ...previous, [key]: value }));
  }
  async function save(event?: FormEvent, archive = false) {
    event?.preventDefault();
    if (saving.current || !canEdit) return;
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      let row: RegistryResource;
      if (resource) {
        if (!resource.revision)
          throw new Error('Не удалось определить версию записи. Обновите реестр.');
        row = archive
          ? await api.archiveRegistryResource(workspaceId, resource.sourceId, resource.revision)
          : await api.updateRegistryResource(workspaceId, resource.sourceId, {
              ...input,
              expectedRevision: resource.revision,
            });
      } else row = await api.createRegistryResource(workspaceId, input);
      if (mounted.current) onSaved(row, archive);
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : 'Не удалось сохранить ресурс');
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={() => !busy && onClose()}>
      <DialogTitle>{resource ? 'Карточка ресурса' : 'Добавить ресурс'}</DialogTitle>
      <form onSubmit={(event) => void save(event)}>
        <DialogContent>
          <Stack gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              select
              label="Тип ресурса"
              value={input.kind}
              disabled={busy || !canEdit}
              onChange={(e) => change('kind', e.target.value as ResourceKind)}
            >
              {RESOURCE_KINDS.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {names[kind]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label={input.kind === 'domain' ? 'Домен' : 'Ссылка'}
              placeholder={input.kind === 'domain' ? 'example.com' : 'https://…'}
              value={input.address}
              disabled={busy || !canEdit}
              onChange={(e) => change('address', e.target.value)}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              helperText={
                input.kind === 'domain'
                  ? input.roles.includes('competitor')
                    ? 'Домен без протокола и пути. Метка конкурента включает все его поддомены; принадлежность — только точный домен.'
                    : 'Точный hostname, без протокола и пути. Поддомены учитываются отдельно.'
                  : 'HTTP(S)-ссылка. Принадлежность относится к этой ссылке; для всего сайта добавьте его домен.'
              }
            />
            <TextField
              required
              label={input.roles.includes('competitor') ? 'Название конкурента' : 'Название'}
              value={input.title}
              disabled={busy || !canEdit}
              onChange={(e) => change('title', e.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <TextField
              select
              label="Принадлежность"
              value={input.ownership}
              disabled={busy || !canEdit}
              onChange={(e) =>
                change('ownership', e.target.value as ManualResourceInput['ownership'])
              }
            >
              {Object.entries(owners).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Роли"
              value={input.roles}
              disabled={busy || !canEdit}
              slotProps={{ select: { multiple: true } }}
              onChange={(e) =>
                change(
                  'roles',
                  (typeof e.target.value === 'string'
                    ? e.target.value.split(',').filter(Boolean)
                    : e.target.value) as ManualResourceInput['roles'],
                )
              }
            >
              {Object.entries(roleNames).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Заметка"
              multiline
              minRows={2}
              value={input.notes}
              disabled={busy || !canEdit}
              onChange={(e) => change('notes', e.target.value)}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
            <Typography variant="body2" color="text.secondary">
              Запись появится в Реестре и будет учитываться в «Карте выдачи». Мониторинг, DNS и
              публикация не запускаются.
            </Typography>
            {confirmArchive && (
              <Alert severity="warning">
                Убрать запись из Реестра? Она перестанет подтверждать принадлежность в «Карте
                выдачи». История сохранится.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          {resource && canDelete && (
            <Button
              color="warning"
              disabled={busy}
              onClick={() =>
                confirmArchive ? void save(undefined, true) : setConfirmArchive(true)
              }
            >
              {confirmArchive ? 'Подтвердить архивирование' : 'В архив'}
            </Button>
          )}
          <Button disabled={busy} onClick={onClose}>
            Закрыть
          </Button>
          {canEdit && (
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !input.title.trim() || !input.address.trim()}
            >
              {busy ? 'Сохранение…' : 'Сохранить'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
